import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../supabaseClient';
import { authService } from '../../../../authService';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Stack,
  Alert,
  Snackbar,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  AccessTime,
  CheckCircle,
  ArrowForward,
  MoveToInbox
} from '@mui/icons-material';

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const LogBookHome = ({ user: propUser }) => {
  const theme = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(propUser || authService.getUserSession());
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0
  });

  // Selection & Actions
  const [selectedReceiveIds, setSelectedReceiveIds] = useState([]);
  const [selectedTransferIds, setSelectedTransferIds] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Dialog States
  const [openTransfer, setOpenTransfer] = useState(false);
  const [openReceive, setOpenReceive] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Sorting State
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('updated_at');

  // Form Data
  const [transferData, setTransferData] = useState({
    department: '',
    transferred_by: '',
    transferred_date: getLocalDateString(),
    reason: ''
  });

  const [receiveData, setReceiveData] = useState({
    received_by: '',
    received_date: getLocalDateString()
  });

  // Filter States
  const [receiveFilter, setReceiveFilter] = useState('All');
  const [transferFilter, setTransferFilter] = useState('All');

  useEffect(() => {
    if (!user) {
        const sessionUser = authService.getUserSession();
        if (sessionUser) setUser(sessionUser);
    }
  }, [propUser]);

  useEffect(() => {
    if (user) {
        setTransferData(prev => ({ ...prev, transferred_by: `${user.firstname} ${user.lastname}` }));
        setReceiveData(prev => ({ ...prev, received_by: `${user.firstname} ${user.lastname}` }));
        fetchTransactions();
        fetchDepartments();
    }
  }, [user]);

  const fetchDepartments = async () => {
    try {
      // Try to fetch unique departments from users table
      const { data, error } = await supabase
        .from('users')
        .select('department')
        .not('department', 'is', null);

      if (error) throw error;
      
      const uniqueDepts = [...new Set(data.map(u => u.department))].sort();
      setDepartments(uniqueDepts);
    } catch (error) {
      console.error('Error fetching departments:', error);
      // Fallback list
      setDepartments(['Mayor\'s Office', 'Budget', 'Accounting', 'Treasury', 'Engineering', 'MPDO', 'HR', 'GSO']);
    }
  };

  const fetchTransactions = async () => {
    if (!user || !user.department) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('elogbook_transactions')
        .select('*')
        .eq('department', user.department)
        .neq('status', 'ready_to_claim')
        .neq('status', 'claimed')
        .neq('status', 'cancelled')
        .order('updated_at', { ascending: false, nullsFirst: false })
        .limit(50);

      if (error) throw error;
      
      const dataList = data || [];
      setTransactions(dataList);

      // Calculate stats
      const pending = dataList.filter(t => t.status === 'pending').length;
      const completed = dataList.filter(t => t.status === 'claimed' || t.status === 'check_issued').length;
      setStats({
        total: dataList.length,
        pending,
        completed
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sorting Helpers
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSortValue = (row, columnId) => {
    if (columnId === 'amount') return row.check_amount || row.voucher_amount || row.cafoa_amount || 0;
    if (columnId === 'reference') return row.check_number || row.dv_number || row.cafoa_number || '';
    if (columnId === 'payee') return row.payee_name || row.payee || '';
    if (columnId === 'type') {
         if (row.check_number) return 'Check';
         if (row.voucher_payee && String(row.voucher_payee).trim() !== '') return 'Voucher';
         return 'CAFOA';
    }
    return row[columnId];
  };

  const getDocumentType = (row) => {
    if (row.check_number) return 'Check';
    if (row.voucher_payee && String(row.voucher_payee).trim() !== '') return 'Voucher';
    return 'CAFOA';
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    const aValue = getSortValue(a, orderBy);
    const bValue = getSortValue(b, orderBy);

    if (bValue < aValue) {
      return order === 'desc' ? -1 : 1;
    }
    if (bValue > aValue) {
      return order === 'desc' ? 1 : -1;
    }
    return 0;
  });

  const documentsToReceive = sortedTransactions
    .filter(t => t.status === 'pending_receive')
    .filter(t => receiveFilter === 'All' || getDocumentType(t) === receiveFilter);
    
  const documentsToTransfer = sortedTransactions
    .filter(t => t.status !== 'pending_receive')
    .filter(t => transferFilter === 'All' || getDocumentType(t) === transferFilter);

  // Selection Handlers
  const handleSelectAll = (event, type) => {
    if (type === 'receive') {
      if (event.target.checked) {
        setSelectedReceiveIds(documentsToReceive.map(n => n.id));
      } else {
        setSelectedReceiveIds([]);
      }
    } else {
      if (event.target.checked) {
        setSelectedTransferIds(documentsToTransfer.map(n => n.id));
      } else {
        setSelectedTransferIds([]);
      }
    }
  };

  const handleSelectOne = (event, id, type) => {
    const selectedIds = type === 'receive' ? selectedReceiveIds : selectedTransferIds;
    const setSelected = type === 'receive' ? setSelectedReceiveIds : setSelectedTransferIds;
    
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  // Action Handlers
  const handleTransfer = async () => {
    if (!transferData.department) {
      setSnackbar({ open: true, message: 'Please select a department', severity: 'error' });
      return;
    }

    if (transferData.department === user.department) {
      setSnackbar({ open: true, message: 'Cannot transfer to the same department', severity: 'error' });
      return;
    }

    setActionLoading(true);
    try {
      // Process each selected transaction
      for (const id of selectedTransferIds) {
        const transaction = transactions.find(t => t.id === id);
        if (!transaction) continue;

        let currentRemarks = [];
        if (transaction.remarks) {
            if (Array.isArray(transaction.remarks)) {
                currentRemarks = [...transaction.remarks];
            } else if (typeof transaction.remarks === 'string') {
                try {
                    const parsed = JSON.parse(transaction.remarks);
                    if (Array.isArray(parsed)) {
                        currentRemarks = parsed;
                    } else {
                        currentRemarks = [{ 
                            content: transaction.remarks, 
                            date: transaction.created_at, 
                            user: 'system' 
                        }];
                    }
                } catch (e) {
                    currentRemarks = [{ 
                        content: transaction.remarks, 
                        date: transaction.created_at, 
                        user: 'system' 
                    }];
                }
            }
        }

        const reasonText = transferData.reason ? ` (Reason: ${transferData.reason})` : '';
        const newRemark = {
          content: `Transferred to ${transferData.department} by ${transferData.transferred_by}${reasonText}`,
          date: transferData.transferred_date,
          user: user.userid,
          user_name: `${user.firstname} ${user.lastname}`
        };

        const { error } = await supabase
          .from('elogbook_transactions')
          .update({
            department: transferData.department,
            status: 'pending_receive',
            remarks: [...currentRemarks, newRemark],
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) throw error;
      }

      setSnackbar({ open: true, message: 'Documents transferred successfully', severity: 'success' });
      setOpenTransfer(false);
      setSelectedTransferIds([]);
      setTransferData(prev => ({ ...prev, department: '', reason: '' }));
      fetchTransactions();
    } catch (error) {
      console.error('Error transferring documents:', error);
      setSnackbar({ open: true, message: 'Error transferring documents', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReceive = async () => {
    setActionLoading(true);
    try {
      for (const id of selectedReceiveIds) {
        const transaction = transactions.find(t => t.id === id);
        if (!transaction) continue;

        let currentRemarks = [];
        if (transaction.remarks) {
            if (Array.isArray(transaction.remarks)) {
                currentRemarks = [...transaction.remarks];
            } else if (typeof transaction.remarks === 'string') {
                try {
                    const parsed = JSON.parse(transaction.remarks);
                    if (Array.isArray(parsed)) {
                        currentRemarks = parsed;
                    } else {
                        currentRemarks = [{ 
                            content: transaction.remarks, 
                            date: transaction.created_at, 
                            user: 'system' 
                        }];
                    }
                } catch (e) {
                    currentRemarks = [{ 
                        content: transaction.remarks, 
                        date: transaction.created_at, 
                        user: 'system' 
                    }];
                }
            }
        }

        const newRemark = {
          content: `Received by ${receiveData.received_by}`,
          date: receiveData.received_date,
          user: user.userid,
          user_name: `${user.firstname} ${user.lastname}`
        };

        const { error } = await supabase
          .from('elogbook_transactions')
          .update({
            status: 'received', // Or restore previous status if tracked, but 'received' is standard
            remarks: [...currentRemarks, newRemark],
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) throw error;
      }

      setSnackbar({ open: true, message: 'Documents received successfully', severity: 'success' });
      setOpenReceive(false);
      setSelectedReceiveIds([]);
      fetchTransactions();
    } catch (error) {
      console.error('Error receiving documents:', error);
      setSnackbar({ open: true, message: 'Error receiving documents', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const getTypeIcon = (row) => {
    if (row.check_number) return <PaymentIcon color="success" />;
    if (row.dv_number) return <ReceiptIcon color="info" />;
    return <DescriptionIcon color="primary" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'claimed':
      case 'check_issued':
      case 'received':
        return 'success';
      case 'obligated':
        return 'info';
      case 'pending':
      case 'pending_receive':
        return 'warning';
      default:
        return 'default';
    }
  };

  const headCells = [
    { id: 'type', label: 'Type' },
    { id: 'reference', label: 'Reference No.' },
    { id: 'payee', label: 'Payee' },
    { id: 'updated_at', label: 'Date' },
    { id: 'amount', label: 'Amount', numeric: true },
    { id: 'status', label: 'Status', align: 'center' },
  ];

  const renderTable = (data, type) => {
    const selectedIds = type === 'receive' ? selectedReceiveIds : selectedTransferIds;
    
    return (
      <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selectedIds.length > 0 && selectedIds.length < data.length}
                    checked={data.length > 0 && selectedIds.length === data.length}
                    onChange={(e) => handleSelectAll(e, type)}
                    disabled={data.length === 0}
                  />
                </TableCell>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.align || (headCell.numeric ? 'right' : 'left')}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                  <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No documents to {type}.
                          </Typography>
                      </TableCell>
                  </TableRow>
              ) : (
                  data.map((row) => {
                    const isSelected = selectedIds.indexOf(row.id) !== -1;
                    return (
                        <TableRow 
                            hover 
                            role="checkbox" 
                            aria-checked={isSelected}
                            tabIndex={-1} 
                            key={row.id}
                            selected={isSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                                color="primary"
                                checked={isSelected}
                                onChange={(event) => handleSelectOne(event, row.id, type)}
                            />
                          </TableCell>
                          <TableCell>{getTypeIcon(row)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {row.cafoa_number || row.dv_number || row.check_number || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.payee_name || row.payee}</TableCell>
                          <TableCell>{new Date(row.updated_at || row.created_at).toLocaleDateString()}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {new Intl.NumberFormat('en-PH', {
                                style: 'currency',
                                currency: 'PHP'
                              }).format(row.cafoa_amount || row.amount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                                label={row.status === 'pending_receive' ? 'For Receive' : row.status} 
                                color={getStatusColor(row.status)}
                                size="small"
                                sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                        </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Department Dashboard
        </Typography>
        <Typography color="text.secondary">
          Overview for {user?.department || 'Unassigned Department'}
        </Typography>
      </Box>
      
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4} md={4}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), boxShadow: 'none' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.main', color: 'white' }}>
                <DescriptionIcon />
              </Box>
              <Box>
                <Typography color="textSecondary" variant="body2">Total Transactions</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), boxShadow: 'none' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'warning.main', color: 'white' }}>
                <AccessTime />
              </Box>
              <Box>
                <Typography color="textSecondary" variant="body2">Pending</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.pending}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), boxShadow: 'none' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.main', color: 'white' }}>
                <CheckCircle />
              </Box>
              <Box>
                <Typography color="textSecondary" variant="body2">Completed</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.completed}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Documents to Receive Section */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: theme.shadows[2], mb: 4 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight="bold" color="warning.dark">
                Documents to Receive
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
                <InputLabel>Document Type</InputLabel>
                <Select
                    value={receiveFilter}
                    label="Document Type"
                    onChange={(e) => setReceiveFilter(e.target.value)}
                >
                    <MenuItem value="All">All Types</MenuItem>
                    <MenuItem value="CAFOA">CAFOA</MenuItem>
                    <MenuItem value="Voucher">Voucher</MenuItem>
                    <MenuItem value="Check">Check</MenuItem>
                </Select>
            </FormControl>
          </Box>
          {selectedReceiveIds.length > 0 && (
            <Button 
                variant="contained" 
                color="success" 
                startIcon={<MoveToInbox />}
                onClick={() => setOpenReceive(true)}
            >
                Receive ({selectedReceiveIds.length})
            </Button>
          )}
        </Box>
        {renderTable(documentsToReceive, 'receive')}
      </Paper>

      {/* Documents to Transfer Section */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: theme.shadows[2] }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight="bold">
                Documents to Transfer
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Document Type</InputLabel>
                <Select
                    value={transferFilter}
                    label="Document Type"
                    onChange={(e) => setTransferFilter(e.target.value)}
                >
                    <MenuItem value="All">All Types</MenuItem>
                    <MenuItem value="CAFOA">CAFOA</MenuItem>
                    <MenuItem value="Voucher">Voucher</MenuItem>
                    <MenuItem value="Check">Check</MenuItem>
                </Select>
            </FormControl>
          </Box>

          {selectedTransferIds.length > 0 && (
            <Button 
                variant="contained" 
                color="primary" 
                startIcon={<ArrowForward />}
                onClick={() => setOpenTransfer(true)}
            >
                Transfer ({selectedTransferIds.length})
            </Button>
          )}
        </Box>
        {renderTable(documentsToTransfer, 'transfer')}
      </Paper>

      {/* Transfer Dialog */}
      <Dialog open={openTransfer} onClose={() => setOpenTransfer(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Documents</DialogTitle>
        <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    You are about to transfer {selectedTransferIds.length} document(s).
                </Typography>
                <Autocomplete
                    options={departments}
                    value={transferData.department}
                    onChange={(_, newValue) => setTransferData({ ...transferData, department: newValue })}
                    renderInput={(params) => <TextField {...params} label="Target Department" required />}
                />
                <TextField
                    label="Transferred By"
                    value={transferData.transferred_by}
                    onChange={(e) => setTransferData({ ...transferData, transferred_by: e.target.value })}
                    fullWidth
                    required
                />
                <TextField
                    label="Date Transferred"
                    type="date"
                    value={transferData.transferred_date}
                    onChange={(e) => setTransferData({ ...transferData, transferred_date: e.target.value })}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="Reason for Transfer"
                    value={transferData.reason}
                    onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Optional"
                />
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenTransfer(false)}>Cancel</Button>
            <Button onClick={handleTransfer} variant="contained" disabled={actionLoading}>
                {actionLoading ? 'Transferring...' : 'Transfer'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={openReceive} onClose={() => setOpenReceive(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Documents</DialogTitle>
        <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    You are about to receive {selectedReceiveIds.length} document(s).
                </Typography>
                <TextField
                    label="Received By"
                    value={receiveData.received_by}
                    onChange={(e) => setReceiveData({ ...receiveData, received_by: e.target.value })}
                    fullWidth
                    required
                />
                <TextField
                    label="Date Received"
                    type="date"
                    value={receiveData.received_date}
                    onChange={(e) => setReceiveData({ ...receiveData, received_date: e.target.value })}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                />
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenReceive(false)}>Cancel</Button>
            <Button onClick={handleReceive} variant="contained" color="success" disabled={actionLoading}>
                {actionLoading ? 'Receiving...' : 'Receive'}
            </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LogBookHome;
